import { Error, error } from "../domain/error";
import { action, computed, observable } from "mobx";
import { Nullable } from "../util/extendedTypes";

export type DataWrapper<T> = T extends Array<any> ? T : Nullable<T>;
export abstract class StoreBase<T> {
  constructor(protected defaultData: () => DataWrapper<T>) {
    this.data = defaultData();
  }

  @observable
  data: DataWrapper<T>;

  @observable
  fetching: boolean = false;

  @observable
  error: Nullable<Error> = null;

  @computed
  get empty() {
    return this.data === null || (this.data as Array<T>).length < 1;
  }

  @action
  reset() {
    this.data = this.defaultData();
  }

  @action
  protected async fetchData(
    fetchFunc: () => Promise<DataWrapper<T>>,
    force = true
  ): Promise<DataWrapper<T>> {
    if (!force && !this.empty) return this.data;
    this.reset();
    this.fetching = true;
    try {
      const data = await fetchFunc();
      this.data = data as DataWrapper<T>;
      this.error = null;
    } catch (err) {
      this.error = error((err as any).code, (err as any).message);
    } finally {
      this.fetching = false;
    }

    return this.data as DataWrapper<T>;
  }

  @action
  async updateData(
    postFunc: () => Promise<DataWrapper<T>>
  ): Promise<DataWrapper<T>> {
    this.fetching = true;
    try {
      const data = await postFunc();
      this.error = null;
      this.data = data;
    } catch (err) {
      this.error = error((err as any).code, (err as any).description);
    } finally {
      this.fetching = false;
    }
    return this.data as DataWrapper<T>;
  }
}
